import { parseMeasureNumber } from '../../utils/attributeMeasure';
import { contributionOf } from './contributionOf';
import {
  offerUidsOf,
  optionKeyOf,
  providerUidOf,
  tagsOf,
  type FilterCategory,
  type FilterOption,
  type FilterSelection,
  type FilterableProduct,
  type FilterType,
} from './productFilterTypes';
import {
  isZeroThatMeansNone,
  readAttributeNumber,
} from './resolveMeasureValue';

const NO_VALUES: readonly string[] = [];

const attributeOf = (product: FilterableProduct, name: string) =>
  product.attributes?.find((a) => a?.name?.name === name) ?? undefined;

/**
 * The uids a product carries in one group — the only place a `filterType` is
 * mapped onto the product shape. Membership only: nothing is re-derived here.
 */
const productValuesFor = (
  filterType: FilterType | undefined,
  product: FilterableProduct,
): readonly string[] => {
  switch (filterType) {
    case 'PROVIDERS': {
      const uid = providerUidOf(product);
      return uid ? [uid] : NO_VALUES;
    }
    case 'OFFERS':
      // Bundle offers count: a reader filtering by an offer means the product
      // carries it, not the particular relation it arrived through.
      return offerUidsOf(product);
    case 'PRODUCT_TAGS':
      return tagsOf(product);
    case 'PRODUCT_ATTRIBUTES':
      // Attribute groups match by comparison rather than by membership, so the
      // uid list is not what decides them — see `matchesAttributeOption`.
      return NO_VALUES;
    default:
      return NO_VALUES;
  }
};

/**
 * `DEFINED` is "present and not false" — the answer given on CMS-1893 Q9, and
 * the reason presence alone is not enough: an attribute can legitimately be
 * `false` or `0`, and a boolean attribute authored `false` must not match.
 */
const isDefined = (raw: unknown): boolean => {
  if (raw == null) return false;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed !== '' && trimmed.toLowerCase() !== 'false';
  }
  return true;
};

/**
 * A comparison against a stored attribute. An unparseable operand or value is
 * "no match" rather than 0 — 0 would make every product look like it qualifies
 * for a zero-fee filter.
 */
const matchesAttributeOption = (
  product: FilterableProduct,
  option: FilterOption,
): boolean => {
  const attribute = attributeOf(product, option.value);

  // `NOT_DEFINED` is the one operator a missing attribute can satisfy, so it is
  // answered before the guard below. It is what "the product states nothing
  // here" is authored as — an absent attribute, which no operand can describe
  // (BRD-03 § 3.2 "No requirement").
  if (option.operator === 'NOT_DEFINED') {
    return (
      !attribute ||
      !isDefined(attribute.value ?? attribute.value_start) ||
      isZeroThatMeansNone(attribute)
    );
  }

  if (!attribute) return false;

  const raw = attribute.value ?? attribute.value_start;
  if (!option.operator || option.operator === 'DEFINED') return isDefined(raw);

  const left = readAttributeNumber(attribute);
  const right = parseMeasureNumber(option.operand);

  if (option.operator === 'BETWEEN') {
    const upper = parseMeasureNumber(option.operandEnd);
    // Both bounds are required: a half-authored band would otherwise widen into
    // an open-ended one and quietly match more than the row claims.
    if (left == null || right == null || upper == null) return false;
    return left >= right && left <= upper;
  }

  if (left == null || right == null) {
    // EQUALS is meaningful on a non-numeric attribute (a dropdown value), so it
    // falls back to a string comparison; the ordering operators cannot.
    return option.operator === 'EQUALS'
      ? String(raw ?? '').trim() === String(option.operand ?? '').trim() &&
          isDefined(raw)
      : false;
  }

  switch (option.operator) {
    case 'EQUALS':
      return left === right;
    case 'LESS_THAN':
      return left < right;
    case 'GREATER_THAN':
      return left > right;
    default:
      return false;
  }
};

/**
 * An attribute group is one of two different things, and the schema says which:
 *
 * - `valuesSourceAttribute` set — the group is about **one** attribute and its
 *   options are that attribute's own values (CMS-1903 R4.1: `LOAN_TYPE` yields
 *   "Balance Transfer", "Fast Cash"). Matching compares the option against
 *   those values, through the same extractor derivation uses.
 * - otherwise — each option **names** an attribute, matched by presence and not
 *   false, or by the option's own comparison (CMS-1893 Q9).
 *
 * Without the first branch a derived group cannot match anything: its options
 * are values, and they were being looked up as attribute names.
 */
const matchesOption = (
  category: FilterCategory,
  option: FilterOption,
  product: FilterableProduct,
): boolean => {
  // A grouped row matches on any member (CMS-2087): its `value` is only the
  // group key and it has no `filterType`, so check members first; this ORs
  // inside the group under either combinator.
  if (option.members) {
    return option.members.some((member) =>
      matchesOption(category, member, product),
    );
  }

  // The option's own kind, so one group can mix them. A derived group has no
  // authored options, so there the group's source is the only thing to read.
  const filterType = option.filterType ?? category.autoGenerateFrom;

  if (filterType !== 'PRODUCT_ATTRIBUTES') {
    return productValuesFor(filterType, product).includes(option.value);
  }

  return category.valuesSourceAttribute
    ? contributionOf(product, category).includes(option.value)
    : matchesAttributeOption(product, option);
};

/**
 * One group's verdict. An empty selection matches everything, so an untouched
 * group never narrows the list.
 *
 * The combinator is per group and editor-controlled (CMS-1903 R3.2): OR means
 * any selected value, AND means all of them — "a product with both features",
 * not "a product in either group".
 */
const matchesCategory = (
  category: FilterCategory,
  selected: readonly string[],
  product: FilterableProduct,
): boolean => {
  if (selected.length === 0) return true;

  const options = new Map(category.options.map((o) => [optionKeyOf(o), o]));
  const chosen = selected.flatMap((value) => {
    const option = options.get(value);
    return option ? [option] : [];
  });
  // A selection naming only unknown values cannot be satisfied, and must not
  // read as "nothing selected" — an old ad URL would then show everything.
  if (chosen.length === 0) return false;

  return category.combinator === 'AND'
    ? chosen.every((option) => matchesOption(category, option, product))
    : chosen.some((option) => matchesOption(category, option, product));
};

/**
 * AND across groups, and within a group whatever its combinator says.
 *
 * `getProduct` mirrors `applyProductSort`'s accessor: a card list holds the
 * product one level down, so the item being filtered is not always the product.
 */
export const applyProductFilter = <T>(
  items: readonly T[],
  categories: readonly FilterCategory[],
  selection: FilterSelection,
  getProduct: (item: T) => FilterableProduct | undefined | null = (item) =>
    item as unknown as FilterableProduct,
): T[] => {
  const active = categories.filter(
    (c) => c.categoryId && (selection[c.categoryId]?.length ?? 0) > 0,
  );
  if (active.length === 0) return [...items];

  return items.filter((item) => {
    const product = getProduct(item);
    // Nothing to match against: kept rather than dropped, so a shape the
    // accessor does not understand cannot silently empty the page.
    if (!product) return true;
    return active.every((category) =>
      matchesCategory(category, selection[category.categoryId!] ?? [], product),
    );
  });
};

/**
 * How many products each option would match, counted against the **full** set
 * rather than the current selection, so a count does not shrink as the reader
 * ticks boxes in the same group.
 */
export const countProductsPerOption = <T>(
  items: readonly T[],
  category: FilterCategory,
  getProduct: (item: T) => FilterableProduct | undefined | null = (item) =>
    item as unknown as FilterableProduct,
): ReadonlyMap<string, number> => {
  const counts = new Map<string, number>();
  category.options.forEach((option) => {
    const n = items.reduce((total, item) => {
      const product = getProduct(item);
      return (
        total + (product && matchesOption(category, option, product) ? 1 : 0)
      );
    }, 0);
    counts.set(optionKeyOf(option), n);
  });
  return counts;
};

export const countActiveFilters = (selection: FilterSelection): number =>
  Object.values(selection).reduce((total, values) => total + values.length, 0);
